import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import PageHeader from '../components/layout/PageHeader';
import authService, {
  authUserFromProfile,
  type CurrentUserProfile,
} from '../services/auth.service';
import ProfileBankAccountsSection from '../components/client/ProfileBankAccountsSection';
import ChangePasswordLink from '../components/client/ChangePasswordLink';
import UsDocumentsSection from '../components/client/UsDocumentsSection';
import { Link } from 'react-router-dom';
import MfaEnrollmentSection from '../components/security/MfaEnrollmentSection';
import PhoneVerificationSection from '../components/security/PhoneVerificationSection';
import SignedInSessions from '../components/security/SignedInSessions';
import { colors } from '../styles/theme';

export default function Profile(): JSX.Element {
  const { user, setUser } = useAuthStore();
  // Pull /auth/me on mount so the page is correct even when the store
  // was populated by an older login (legacy persisted state without
  // first_name / last_name / timezone). Falls back to the store when
  // the request fails so the user still sees something.
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    authService.getCurrentUser().then((p) => {
      if (cancelled) return;
      setProfile(p);
      // Hydrate the store too — next read by UserMenu / Dashboard
      // subtitle benefits without an extra round-trip. `setUser`
      // touches only the user payload; tokens are untouched.
      setUser(authUserFromProfile(p));
    }).catch(() => { /* silent — keep falling back to store */ });
    return () => { cancelled = true; };
  }, [setUser]);

  const email = profile?.email ?? user?.email ?? '—';
  const firstName = profile?.first_name ?? user?.first_name ?? '—';
  const lastName = profile?.last_name ?? user?.last_name ?? '—';
  const role = profile?.role ?? user?.role ?? '—';
  const timezone = profile?.timezone ?? '—';
  const isClient = role === 'client';
  const clientId = profile?.id ?? null;

  return (
    <>
      <PageHeader title="Profile" subtitle="Account details and security settings" />
      <div
        className="rounded-2xl p-6 max-w-2xl"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-6">
          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            Email
          </dt>
          <dd className="sm:col-span-2 text-sm" style={{ color: colors.ink }}>
            {email}
          </dd>

          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            First name
          </dt>
          <dd className="sm:col-span-2 text-sm" style={{ color: colors.ink }}>
            {firstName}
          </dd>

          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            Last name
          </dt>
          <dd className="sm:col-span-2 text-sm" style={{ color: colors.ink }}>
            {lastName}
          </dd>

          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            Role
          </dt>
          <dd className="sm:col-span-2 text-sm" style={{ color: colors.ink }}>
            {role}
          </dd>

          <dt className="text-xs uppercase tracking-wide" style={{ color: colors.muted }}>
            Timezone
          </dt>
          <dd className="sm:col-span-2 text-sm" style={{ color: colors.ink }}>
            {timezone}
          </dd>
        </dl>
      </div>

      <ChangePasswordLink />

      <MfaEnrollmentSection />

      <PhoneVerificationSection />

      <SignedInSessions />

      {isClient && clientId && (
        <ProfileBankAccountsSection clientId={clientId} />
      )}

      {isClient && <UsDocumentsSection />}

      {isClient && (
        <section
          className="rounded-2xl p-6 max-w-2xl mt-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h2 className="text-lg font-semibold" style={{ color: colors.ink }}>Your personal data</h2>
          <p className="text-sm mt-1" style={{ color: colors.muted }}>
            You can ask to see, correct, delete or object to the use of the personal data we hold about you.
          </p>
          <Link
            to="/client/data-requests"
            className="inline-block mt-3 text-sm font-semibold underline"
            style={{ color: colors.inkSoft }}
          >
            Make a request about my data
          </Link>
        </section>
      )}
    </>
  );
}
